import {
  BooleanInput,
  CheckboxGroupInput,
  Create,
  CreateContextProvider,
  DateTimeInput,
  Form,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  NumberInput,
  Toolbar,
  TopToolbar,
  useCreateController,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Card,
  Divider,
  Stack,
  Typography,
  Button as MUIButton,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Resources } from '../resource';

import CustomTagInput from 'src/pages/admin/components/CustomTagInput';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useAgents from './useAgents';

interface FormProps {
  isCreate: boolean;
}
const AgentsForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();
  const redirect = useRedirect();
  const { watch, setValue, trigger } = useFormContext();
  const record = useRecordContext();
  const { findAgents } = useAgents();
  const [addressMessage, setAddressMessage] = useState('');
  const agentId = useWatch({ name: 'agentId' });
  const [noCreate, setNoCreate] = useState<boolean>(true);

  const loadForEdit = useCallback(async () => {
    if (isCreate) return;
    const curId = (agentId ?? '').trim();
    if (!curId) return;

    setAddressMessage('');
    try {
      const agent = await findAgents(curId);
      if (!agent) return;

      setNoCreate(true);
      setValue('name', agent.name);
      setValue('directCommissionRate', agent.directCommissionRate);
      setValue('contractVolumeThreshold', agent.contractVolumeThreshold);
      setValue('extendedCommissionRate', agent.extendedCommissionRate);
      setValue('indirectCommissionRate', agent.indirectCommissionRate);
      setValue('makerFeeRate', agent.makerFeeRate);
      setValue('memeFeeRate', agent.memeFeeRate);
      setValue('memeFeeRebate', agent.memeFeeRebate);
      setValue('memeVolumeThreshold', agent.memeVolumeThreshold);
      setValue('takerFeeRate', agent.takerFeeRate);
      // setValue('totalCommission', agent.totalCommission);
      // setValue('totalVolumeUsd', agent.totalVolumeUsd);
      // setValue('userCount', agent.userCount);
    } catch {
      setAddressMessage(t('module.agents.validate.required'));
    }
  }, [isCreate, agentId, findAgents, setValue, t]);

  useEffect(() => {
    loadForEdit();
  }, [loadForEdit]);

  const hasTypedRef = useRef(false);

  const numberProps = {
    parse: (v: any) => (v === '' || v == null ? undefined : Number(v)),
    format: (v: any) => (v == null ? '' : v),

    inputProps: {
      inputMode: 'decimal',
      step: 'any',
      onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
        (e.currentTarget as HTMLInputElement).blur();
      },
    } as const,

    onWheel: (e: React.WheelEvent) => {
      const input = (e.currentTarget as HTMLElement).querySelector('input');
      if (input) (input as HTMLInputElement).blur();
    },
  } as const;

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.name')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="name"
              validate={[required()]}
              placeholder={t('module.agents.name')}
              InputProps={{ readOnly: !isCreate }}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="string"
            />
          </Box>
        </Stack>

        {/* <Stack direction={'row'} alignItems={'center'} spacing={2} mb={1}>
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.status')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <BooleanInput
              label=""
              source="status"
              helperText={<span style={{ color: 'red' }}>{''}</span>}
            />
          </Box>
        </Stack> */}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.directCommissionRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="directCommissionRate"
              validate={[required()]}
              placeholder={t('module.agents.directCommissionRate')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.contractVolumeThreshold')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="contractVolumeThreshold"
              placeholder={t('module.agents.contractVolumeThreshold')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.extendedCommissionRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="extendedCommissionRate"
              placeholder={t('module.agents.extendedCommissionRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.indirectCommissionRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="indirectCommissionRate"
              placeholder={t('module.agents.indirectCommissionRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.makerFeeRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="makerFeeRate"
              placeholder={t('module.agents.makerFeeRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.memeFeeRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="memeFeeRate"
              placeholder={t('module.agents.memeFeeRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.memeFeeRebate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="memeFeeRebate"
              placeholder={t('module.agents.memeFeeRebate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.memeVolumeThreshold')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="memeVolumeThreshold"
              placeholder={t('module.agents.memeVolumeThreshold')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.takerFeeRate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="takerFeeRate"
              placeholder={t('module.agents.takerFeeRate')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>

        {/* <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.totalCommission')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="totalCommission"
              placeholder={t('module.agents.totalCommission')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agents.totalVolumeUsd')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              label={false}
              source="totalVolumeUsd"
              placeholder={t('module.agents.totalVolumeUsd')}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
          mt={2}
        >
          <Box sx={{ flexBasis: '25%', flexShrink: 0 }}>
            <Typography variant="body2"> {t('module.agents.userCount')}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <NumberInput
              source="userCount"
              label=""
              placeholder={t('module.agents.userCount')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              {...numberProps}
            />
          </Box>
        </Stack> */}
      </Stack>
    </>
  );
};

export default AgentsForm;
